import { Fragment, useState } from "react";
import { NavLink } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";

import { XMarkIcon, ChevronLeftIcon } from "@heroicons/react/24/outline";

import classNames from "../../../utils/classNames";
import WilmaLogo from "../../../assets/wilma-logo.svg";
import { NavigationItemInterface } from "../../../types/Navigation";
import useIsMobile from "../../../hooks/useIsMobile";
import { useAuth } from "../../../contexts/AuthContext";
import { impersonationTheme } from "../../../utils/impersonation";

export interface MobileSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  navigation: NavigationItemInterface[];
}

function getNavItem(item: NavigationItemInterface, setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>, branded: boolean) {
  if (item.disabled) {
    return (
      <div key={item.name} className="group flex items-center px-2 py-2 text-sm text-gray-400 font-medium rounded-md select-none">
        <item.icon
          className="mr-3 h-6 w-6 flex-shrink-0 text-currentColor"
          aria-hidden="true"
        />
        {item.name}
      </div>
    );
  }

  return (
    <NavLink
      key={item.name}
      to={item.to}
      onClick={() => setSidebarOpen(false)}
      className={({ isActive }) =>
        classNames(
          "group flex items-center px-2 py-2 text-sm font-medium rounded-md",
          branded ? "" : "text-white",
          isActive ? (branded ? "bg-black/20" : "bg-primary-700") : ""
        )
      }
    >
      <item.icon
        className="mr-3 h-6 w-6 flex-shrink-0 text-currentColor"
        aria-hidden="true"
      />
      {item.name}
    </NavLink>
  );
}

export default function MobileSidebar({
  sidebarOpen,
  setSidebarOpen,
  navigation,
}: MobileSidebarProps) {
  const isMobile = useIsMobile();
  const { impersonating } = useAuth();
  // Same brand colouring as the desktop sidebar while SAV is viewing as a customer
  const theme = impersonationTheme(impersonating);
  let groups = [...new Set(navigation.map((x) => x.group))];
  const [groupToggle, setGroupToggle] = useState(
    Array.from({ length: 10 }, () => true)
  );

  const toggleGroup = (i: number) => {
    let ng = [...groupToggle];
    ng[i] = !ng[i];
    setGroupToggle(ng);
  };

  return (
    <Transition.Root show={isMobile && sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40 md:hidden"
        onClose={setSidebarOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel
              className={classNames("relative flex w-full max-w-xs flex-1 flex-col pt-5 pb-4", theme ? "" : "bg-primary-600")}
              style={theme ? { backgroundColor: theme.background, color: theme.text } : undefined}
            >
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex flex-shrink-0 justify-center items-center px-4 mt-4 mb-8">
                <img className={classNames("h-8 w-auto", theme && theme.text !== "#FFFFFF" ? "invert" : "")} src={WilmaLogo} alt="Wilma Logo" />
              </div>
              <div className="mt-5 h-0 flex-1 overflow-y-auto">
                <div className="flex flex-col mb-8">
                  {groups.map((group, i) => (
                    <nav key={i} className="flex-1 space-y-1 px-2 pb-4">
                      {group === null || group === undefined ? (
                        <></>
                      ) : (
                        <div
                          className={classNames(
                            "flex flex-row items-center px-2 text-md font-medium rounded-md text-currentColor cursor-pointer",
                            theme ? "opacity-80" : "text-primary-100"
                          )}
                          onClick={() => toggleGroup(i)}
                        >
                          <span>{group}</span>
                          <span
                            className="w-4 h-4 ml-2 transition duration-500"
                            style={{
                              transform: groupToggle[i]
                                ? "rotate(270deg)"
                                : "rotate(90deg)",
                            }}
                          >
                            <ChevronLeftIcon />
                          </span>
                        </div>
                      )}
                      {groupToggle[i]
                        ? navigation
                            .filter((x) => x.group === group)
                            .map((n) => getNavItem(n, setSidebarOpen, !!theme))
                        : null}
                    </nav>
                  ))}
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
          <div className="w-14 flex-shrink-0" aria-hidden="true">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
